const clean = (value) => String(value ?? "").trim();

const sameValue = (left, right) => {
  const a = clean(left).toLowerCase();
  const b = clean(right).toLowerCase();
  return Boolean(a && b && a === b);
};

const findCategory = (categoryTree, value = {}) =>
  (categoryTree || []).find(
    (category) =>
      sameValue(category.id, value.category_id) ||
      sameValue(category.slug, value.category_slug) ||
      sameValue(category.name, value.category),
  );

const findSubcategory = (category, value = {}) =>
  (category?.children || []).find(
    (subcategory) =>
      sameValue(subcategory.id, value.subcategory_id || value.id) ||
      sameValue(subcategory.slug, value.subcategory_slug || value.slug) ||
      sameValue(subcategory.name, value.subcategory || value.name),
  );

const subcategoryRecord = (subcategory, fallback = {}) => ({
  subcategory_id: subcategory?.id || fallback.subcategory_id || fallback.id || null,
  subcategory: subcategory?.name || fallback.subcategory || fallback.name || "",
  subcategory_slug: subcategory?.slug || fallback.subcategory_slug || fallback.slug || null,
});

export const categoryAssignmentKey = (assignment = {}) =>
  clean(assignment.category_id || assignment.category_slug || assignment.category).toLowerCase();

export const subcategoryAssignmentKey = (subcategory = {}) =>
  clean(
    subcategory.subcategory_id ||
      subcategory.id ||
      subcategory.subcategory_slug ||
      subcategory.slug ||
      subcategory.subcategory ||
      subcategory.name,
  ).toLowerCase();

export const buildCategoryAssignment = (category, selectedChildren = []) => ({
  category_id: category?.id || null,
  category: category?.name || "",
  category_slug: category?.slug || null,
  subcategory_ids: selectedChildren.map((child) => child.id).filter(Boolean),
  subcategories: selectedChildren.map((child) => subcategoryRecord(child)),
});

/**
 * Converts both legacy single-category products and grouped multi-category
 * products into the current API contract. Saved names/slugs are retained when
 * a category was removed from the current tree, so opening an old product does
 * not silently discard its catalog placement.
 */
export function normalizeProductCategoryAssignments(product = {}, categoryTree = []) {
  const rawAssignments = Array.isArray(product.category_assignments) && product.category_assignments.length
    ? product.category_assignments
    : product.category || product.category_id || product.category_slug
      ? [
          {
            category_id: product.category_id,
            category: product.category,
            category_slug: product.category_slug,
            subcategory_ids: product.subcategory_id ? [product.subcategory_id] : [],
            subcategories:
              product.subcategory || product.subcategory_id || product.subcategory_slug
                ? [
                    {
                      subcategory_id: product.subcategory_id,
                      subcategory: product.subcategory,
                      subcategory_slug: product.subcategory_slug,
                    },
                  ]
                : [],
          },
        ]
      : [];

  const normalized = [];

  rawAssignments.filter(Boolean).forEach((raw) => {
    const category = findCategory(categoryTree, raw);
    const rawSubcategories = Array.isArray(raw.subcategories) ? raw.subcategories.filter(Boolean) : [];
    const rawIds = Array.isArray(raw.subcategory_ids) ? raw.subcategory_ids.filter(Boolean) : [];
    const subcategoryInputs = [
      ...rawSubcategories,
      ...rawIds.map((id) => ({ subcategory_id: id })),
    ];
    const uniqueSubcategories = [];
    const seenSubcategories = new Set();

    subcategoryInputs.forEach((entry) => {
      const canonical = findSubcategory(category, entry);
      const record = subcategoryRecord(canonical, entry);
      const key = subcategoryAssignmentKey(record);
      if (!key || seenSubcategories.has(key)) return;
      seenSubcategories.add(key);
      uniqueSubcategories.push(record);
    });

    const assignment = {
      category_id: category?.id || raw.category_id || null,
      category: category?.name || raw.category || "",
      category_slug: category?.slug || raw.category_slug || null,
      subcategory_ids: uniqueSubcategories.map((item) => item.subcategory_id).filter(Boolean),
      subcategories: uniqueSubcategories,
    };
    const key = categoryAssignmentKey(assignment);
    if (!key) return;

    const existing = normalized.find((item) => categoryAssignmentKey(item) === key);
    if (!existing) {
      normalized.push(assignment);
      return;
    }

    const existingKeys = new Set(existing.subcategories.map(subcategoryAssignmentKey));
    assignment.subcategories.forEach((subcategory) => {
      const subKey = subcategoryAssignmentKey(subcategory);
      if (!subKey || existingKeys.has(subKey)) return;
      existingKeys.add(subKey);
      existing.subcategories.push(subcategory);
      if (subcategory.subcategory_id) existing.subcategory_ids.push(subcategory.subcategory_id);
    });
  });

  return normalized;
}

export function assignmentContainsSubcategory(assignment, subcategory) {
  const wanted = subcategoryAssignmentKey(subcategory);
  if (!wanted) return false;
  return (assignment?.subcategories || []).some((item) => subcategoryAssignmentKey(item) === wanted) ||
    (assignment?.subcategory_ids || []).some((id) => sameValue(id, subcategory.id || subcategory.subcategory_id));
}

/**
 * Keeps the original single-category fields in sync for older storefront,
 * order and reporting code. The first selected placement is intentionally the
 * primary placement; all placements remain available in category_assignments.
 */
export function derivePrimaryCategoryFields(categoryAssignments = [], categoryTree = []) {
  const assignments = normalizeProductCategoryAssignments(
    { category_assignments: categoryAssignments },
    categoryTree,
  );
  const primary = assignments[0] || null;
  const primarySubcategory = primary?.subcategories?.[0] || null;

  return {
    category_assignments: assignments,
    category: primary?.category || "",
    category_id: primary?.category_id || null,
    category_slug: primary?.category_slug || null,
    subcategory: primarySubcategory?.subcategory || "",
    subcategory_id: primarySubcategory?.subcategory_id || null,
    subcategory_slug: primarySubcategory?.subcategory_slug || null,
  };
}

/** Storefront-safe matching that also understands products created before the
 * multi-category feature existed. */
export function productMatchesCategory(product = {}, categorySlug, subcategorySlug = null) {
  const wantedCategory = clean(categorySlug).toLowerCase();
  const wantedSubcategory = clean(subcategorySlug).toLowerCase();
  if (!wantedCategory) return true;

  const assignments = normalizeProductCategoryAssignments(product);
  return assignments.some((assignment) => {
    const categoryMatches = sameValue(assignment.category_slug, wantedCategory) ||
      sameValue(assignment.category_id, wantedCategory) ||
      sameValue(assignment.category, wantedCategory);
    if (!categoryMatches) return false;
    if (!wantedSubcategory) return true;
    return (assignment.subcategories || []).some(
      (subcategory) =>
        sameValue(subcategory.subcategory_slug, wantedSubcategory) ||
        sameValue(subcategory.subcategory_id, wantedSubcategory) ||
        sameValue(subcategory.subcategory, wantedSubcategory),
    );
  });
}
